import { NavigationBar } from '@travelnest/hui';
import React from 'react';
import { useStore } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { IProperty } from '../../../data/entities/property';
import { actions as entityActions } from '../../../data/store';
import { isAdmin } from '../../../entities/admin/selectors';
import { currentUserId } from '../../../entities/auth/session/selectors';
import { IThunkDispatch } from '../../../store/middleware';
import { IState } from '../../../store/reducer';
import { Loader } from '../../../view/components/loader/Loader';
import { useCustomersProperties, useCustomersPropertyScores, useCustomersSelectedPropertyScore, useScrollToTop } from './hooks';
import { PropertyScoreForProperty } from './PropertyScoreForProperty';

const MAX_RETRIES = 5;

export const PropertyScorePage: React.FC = () => {
	useScrollToTop();
	const navigate = useNavigate();
	const { getState, dispatch }: { getState: () => IState, dispatch: IThunkDispatch } = useStore();
	const adminUser = isAdmin(getState());
	const customerId = currentUserId(getState());
	const { id: currentPropertyId } = useParams<{ id: string }>();
	const { selectedPropertyScore, loading: selectedPropertyScoreLoading, scoreDiff } = useCustomersSelectedPropertyScore(Number(currentPropertyId));
	const { propertyScores, loading: propertyScoresLoading } = useCustomersPropertyScores(customerId, (!!scoreDiff && scoreDiff !== 0));
	const { customerProperties, loading: customerPropertiesLoading } = useCustomersProperties(customerId);
	const selectedProperty = customerProperties.find((p: IProperty) => p.id.toString() === currentPropertyId);
	const snoozeRecommendation = (custId: string, propertyId: number, attributeName: string) =>
		dispatch(entityActions.propertyScores.snoozeByPropertyId(custId, propertyId, attributeName));
	const markAttributesAsViewed = (custId: string, propertyId: number) => {
		if (!adminUser) {
			dispatch(entityActions.propertyScores.markAttributesAsViewedByPropertyId(custId, propertyId));
		}
	};
	const loading = propertyScoresLoading || customerPropertiesLoading || selectedPropertyScoreLoading;
	const [activeSegment, setActiveSegment] = React.useState<number>(0);
	const [publishing, setPublishing] = React.useState<boolean>(false);

	return (
		<div className="portal-wrapper">
			<NavigationBar title="Property score" />
			{
				loading
					? <div style={{ height: '100vh' }}><Loader loading={loading} /></div>
					:
					<PropertyScoreForProperty
						selectableProperties={customerProperties}
						selectablePropertyScores={propertyScores}
						selectedProperty={selectedProperty!}
						selectedPropertyScore={selectedPropertyScore}
						selectedPropertyScoreDiff={scoreDiff}
						loading={loading}
						customerId={customerId}
						activeSegment={activeSegment}
						currentlyPublishing={publishing}
						onChangeProperty={async (id: string) => {
							const property = customerProperties.find((p) => (p.id.toString() === id) || p.legacy_property_id === id);
							try {
								await dispatch(entityActions.propertyScores.getByPropertyId(property!.id));
							} catch {
								// global error will catch anything that throws
							} finally {
								navigate(`/property-scores/${property!.id}/score`);
								setActiveSegment(0);
							}
						}}
						onPublishProperty={async (id: number) => {
							try {
								setPublishing(true);
								await dispatch(entityActions.property.publish(id));
							} catch {
								// global error will catch anything that throws
							} finally {
								for (let i = 0; i < MAX_RETRIES; i++) {
									// cant use scoreDiff here as it will always be 0 at time of entering this scope
									const score = Number(sessionStorage.getItem(`${selectedPropertyScore.property_id}-current-property-score`));
									const diff = selectedPropertyScore.score - score;
									if (diff !== 0) {
										setPublishing(false);
										break;
									}
									if (diff === 0) {
										dispatch(entityActions.propertyScores.getByPropertyId(id));
										await new Promise((r) => setTimeout(r, 1000));
									}
									if (i === MAX_RETRIES - 1) {
										setPublishing(false);
									}
								}
							}
						}}
						snoozeRecommendation={snoozeRecommendation}
						markAttributesAsViewed={markAttributesAsViewed}
						setActiveSegment={setActiveSegment}
					/>
			}
		</div>
	);
};
